import React, { useEffect, useMemo, useRef } from 'react';
import { Alert, Box, CircularProgress, Typography } from '@mui/material';
import { Background, Controls, ReactFlow, MarkerType } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { AIRMASK_GREEN } from '../constants/colors';

interface GraphNode {
  id: string;
  label: string;
  habitNames?: string[];
  color?: string;
}

interface GraphEdge {
  source: string;
  target: string;
}

interface GraphPayload {
  success: boolean;
  nodes?: GraphNode[];
  edges?: GraphEdge[];
  shortestPath?: string[];
  error?: string;
  code?: string;
}

interface BlockNodeGraphProps {
  graph?: GraphPayload;
  habitName?: string;
  title?: string;
}

type ReactFlowController = {
  fitView: (options?: { padding?: number; duration?: number }) => void;
};

const HABIT_COLORS: Record<string, string> = {
  'increase luck': '#f59e0b',
  'blocks: increase luck': '#f59e0b',
  create: '#29b6f6',
  learn: '#ffa726',
  manage: AIRMASK_GREEN,
};

const normalizeHabitName = (value?: string) => value?.trim().toLowerCase().replace(/^blocks\s*:\s*/, '').trim() ?? '';

const matchesHabit = (habitName?: string, targetHabit?: string) => {
  const normalizedHabit = normalizeHabitName(habitName);
  const normalizedTarget = normalizeHabitName(targetHabit);
  return normalizedHabit !== '' && normalizedTarget !== '' && normalizedHabit === normalizedTarget;
};

const BlockNodeGraph: React.FC<BlockNodeGraphProps> = ({ graph, habitName, title = 'Block Nodes' }) => {
  const reactFlowInstance = useRef<ReactFlowController | null>(null);
  const lastLayoutKeyRef = useRef<string>('');

  const visibleNodes = useMemo(() => {
    const baseNodes = graph?.nodes ?? [];
    const normalizedHabit = normalizeHabitName(habitName);

    if (!normalizedHabit) {
      return baseNodes;
    }

    return baseNodes.filter((node) => (node.habitNames ?? []).some((habit) => matchesHabit(habit, habitName)));
  }, [graph?.nodes, habitName]);

  const visibleEdges = useMemo(() => {
    const visibleNodeIds = new Set(visibleNodes.map((node) => node.id));
    return (graph?.edges ?? []).filter((edge) => visibleNodeIds.has(edge.source) && visibleNodeIds.has(edge.target));
  }, [graph?.edges, visibleNodes]);

  const nodeColor = (node: GraphNode) => {
    if (node.color) {
      return node.color;
    }
    const matchingHabit = (node.habitNames ?? []).find((habit) => HABIT_COLORS[normalizeHabitName(habit)] !== undefined);
    if (matchingHabit) {
      return HABIT_COLORS[normalizeHabitName(matchingHabit)] ?? AIRMASK_GREEN;
    }
    return AIRMASK_GREEN;
  };

  const nodes = useMemo(() => visibleNodes.map((node, index) => ({
    id: node.id,
    position: { x: 80 + (index % 3) * 180, y: 60 + Math.floor(index / 3) * 120 },
    data: { label: node.label },
    style: {
      backgroundColor: nodeColor(node),
      border: '2px solid rgba(0,0,0,0.15)',
      color: '#fff',
      borderRadius: 12,
      width: 96,
      minHeight: 56,
      padding: 12,
      fontWeight: 700,
    },
    className: 'block-node',
  })), [visibleNodes]);

  const edges = useMemo(() => visibleEdges.map((edge) => ({
    id: `${edge.source}-${edge.target}`,
    source: edge.source,
    target: edge.target,
    animated: false,
    markerEnd: { type: MarkerType.ArrowClosed },
    style: { stroke: '#32CA1D', strokeWidth: 2 },
  })), [visibleEdges]);

  const layoutKey = useMemo(() => {
    const nodeIds = nodes.map((node) => node.id).join('|');
    const edgeIds = edges.map((edge) => edge.id).join('|');
    return `${nodeIds}::${edgeIds}`;
  }, [nodes, edges]);

  useEffect(() => {
    if (!reactFlowInstance.current || nodes.length === 0) {
      return;
    }

    if (lastLayoutKeyRef.current === layoutKey) {
      return;
    }
    lastLayoutKeyRef.current = layoutKey;

    const frame = window.requestAnimationFrame(() => {
      reactFlowInstance.current?.fitView({ padding: 0.2, duration: 0 });
    });

    return () => window.cancelAnimationFrame(frame);
  }, [layoutKey, nodes.length]);

  if (!graph) {
    return (
      <Box sx={{ p: 2, border: '1px solid rgba(50, 202, 29, 0.2)', borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size={18} sx={{ color: '#32CA1D' }} />
          <Typography variant="body2" color="text.secondary">Loading graph…</Typography>
        </Box>
      </Box>
    );
  }

  if (!graph.success) {
    return (
      <Box sx={{ p: 2, border: '1px solid rgba(50, 202, 29, 0.2)', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
        <Alert severity="error">{graph.error || 'Unable to load block graph.'}</Alert>
      </Box>
    );
  }

  if (visibleNodes.length === 0) {
    return (
      <Box sx={{ p: 2, border: '1px solid rgba(50, 202, 29, 0.2)', borderRadius: 1 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">No matching block nodes found.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2, border: '1px solid rgba(50, 202, 29, 0.2)', borderRadius: 1, bgcolor: 'rgba(255,255,255,0.03)' }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
      <Box sx={{ height: 320, width: '100%', borderRadius: 1, overflow: 'hidden', bgcolor: '#f6fbf3' }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onInit={(instance: ReactFlowController) => {
            reactFlowInstance.current = instance;
            if (nodes.length > 0) {
              window.requestAnimationFrame(() => instance.fitView({ padding: 0.2, duration: 0 }));
            }
          }}
          nodesDraggable={false}
          nodesConnectable={false}
          elementsSelectable={false}
          proOptions={{ hideAttribution: true }}
          style={{ width: '100%', height: '100%' }}
        >
          <Background gap={16} color="rgba(50, 202, 29, 0.2)" />
          <Controls />
        </ReactFlow>
      </Box>
    </Box>
  );
};

export default BlockNodeGraph;
